/**
 * LuaIdeModule — the Lua scripting IDE (I key), v3: a full editor + copilot.
 *
 * Editor (CodeMirror 6): Lua highlighting, API-aware autocomplete + hover docs
 * (lib/ide/lua_api.ts — ONE catalog for both dialects), live diagnostics
 * (lib/ide/lua_lint.ts + server compile errors mapped inline), search panel
 * (Ctrl-F), snippets, Ctrl-S deploy.
 *
 * v3 — the Visual-Studio feature wave (Roblox Studio / UEFN informed):
 *   · ▶ Run / ■ Stop — the buffer runs INSTANTLY in the local sandbox via
 *     `atlas:script-preview` (only you see it; Deploy publishes) — the UEFN
 *     "push changes" live-iteration loop
 *   · Problems panel (click-to-jump), Console tab (world.log + errors)
 *   · Outline panel (functions/hooks/locals via lib/ide/lua_tools.ts)
 *   · Objects panel — the script↔object bridge: world parts + prefab keys,
 *     click-to-insert ids, 🎯 pick-in-world (click a part in 3D → id at
 *     cursor), per-part "new script" template (the LSL new-script-in-object
 *     loop, browser-shaped)
 *   · Format Document (Shift-Alt-F), Rename symbol (F2), Go to definition
 *     (F12 / Ctrl-click), toggle comment (Ctrl-/), go to line (Ctrl-G)
 *   · Command palette (Ctrl-Shift-P / F1)
 *   · Draft autosave per world (localStorage) + dirty dots in the rail
 *
 * AI copilot (lib/ide/copilot.ts — Anthropic/OpenAI, USER key in localStorage):
 *   · Chat (streaming, world-code context) with per-code-block Insert / Apply
 *   · Inline chat (Ctrl-I) — rewrite the SELECTION in place, diff-reviewed
 *   · Edit — full-buffer rewrite reviewed as a unified diff (accept/reject
 *     per chunk); the model always outputs the WHOLE file (the Val Town rule)
 *   · Agent — bounded tool loop: list/read scripts, lint, list objects, read
 *     the console, RUN code in the live sandbox, stage edits for review
 *   · Ghost text — debounced inline completions (Tab accepts)
 *   · Slash commands: /explain /fix /doc /run
 */

import { EditorView, basicSetup } from 'codemirror';
import { keymap, hoverTooltip } from '@codemirror/view';
import { Compartment } from '@codemirror/state';
import { indentWithTab } from '@codemirror/commands';
import { StreamLanguage } from '@codemirror/language';
import { lua } from '@codemirror/legacy-modes/mode/lua';
import { oneDark } from '@codemirror/theme-one-dark';
import { autocompletion, snippetCompletion, type CompletionContext, type CompletionResult, type Completion } from '@codemirror/autocomplete';
import { linter, lintGutter, forceLinting, type Diagnostic } from '@codemirror/lint';
import { search, openSearchPanel, gotoLine } from '@codemirror/search';
import { unifiedMergeView } from '@codemirror/merge';
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { LUA_API, apiFor, apiGroups, searchApi, apiPromptBlock, LUA_KEYWORDS, LUA_STDLIB, type LuaDialect } from '../lib/ide/lua_api.js';
import { NPC_TEMPLATES } from '../lib/npc_script_templates.js';
import type { NpcUnitModule } from './NpcUnitModule.js';
import { lintLua, diagnosticFromServerError, type LuaDiagnostic } from '../lib/ide/lua_lint.js';
import { formatLua, outlineLua, findOccurrences, findDefinition } from '../lib/ide/lua_tools.js';
import { loadSettings, saveSettings, chatStream, runAgent, completeInline, DEFAULT_MODEL, type CopilotSettings, type ChatMsg, type ToolDef } from '../lib/ide/copilot.js';
import { ghostText } from '../lib/ide/ghost_text.js';
import { diffLines, diffStats } from '../lib/ide/diff.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';

const CAPS = ['objects.read', 'objects.write', 'world.events.emit', 'tickets.issue', 'player.pulse.read', 'economy.tip'] as const;

const WORLD_TEMPLATE = `-- World script (CLIENT-side, engine-sandboxed; runs on every visitor here)
-- ▶ Run tests it instantly (only you see it). Deploy (Ctrl-S) publishes it.
-- Open the API panel (API button) for every world.* function with docs.
local sign = nil
local t = 0
return {
  on_init = function()
    local h = world.terrain_height(0, 0)
    sign = world.spawn("prop:holo_signpost", 0, h, 0)
    world.label("Scripted by the Lua IDE", 0, h + 4, 0)
  end,
  on_tick = function(dt)
    t = t + dt
    world.set(sign, 0, world.terrain_height(0, 0), 0, t * 0.6)
  end,
  on_event = function(type, payload)
    world.log("contract event: " .. type)
    world.particles(0, world.terrain_height(0, 0) + 2, 0, 80, "ffd27f", 5)
    world.tone(660, 120)
  end,
}
`;

const TEMPLATE = `-- Cosmos contract (runs SERVER-side; effects replicate to everyone)
-- Events: interact | enter | tick,N | signal,"name"
contract.on("interact", function(ev)
  local open = state.get("open")
  if open == 1 then
    state.set("open", 0)
    world.emit("door.closed")
  else
    state.set("open", 1)
    world.emit("door.opened")
  end
end)
`;

const objectTemplate = (partId: string, primType: string): string => `-- Script for ${primType} part #${partId} (created from the Objects panel)
local part = "${partId}"
local glow = nil
return {
  on_init = function()
    local x, y, z = world.part_x(part), world.part_y(part), world.part_z(part)
    glow = world.light(x, y + 2, z, "7fd0ff", 20, 12)
    world.attach(glow, part)
  end,
  on_tick = function(dt)
    local d = world.dist(world.my_x(), world.my_y(), world.my_z(),
      world.part_x(part), world.part_y(part), world.part_z(part))
    if d < 3 and world.key_down("e") then
      world.particles(world.part_x(part), world.part_y(part) + 1, world.part_z(part), 60, "ffd27f", 4)
      world.tone(660, 100)
    end
  end,
}
`;

interface LuaReducers {
  compileLuaContract(a: { worldId: bigint; assetId: bigint; luaSource: string; capsRequested: string }): Promise<void>;
  setWorldScript(a: { worldId: bigint; name: string; source: string; enabled: boolean }): Promise<void>;
  deleteWorldScript(a: { scriptId: bigint }): Promise<void>;
  createAsset(a: {
    kind: string; contentRef: string; permCopy: boolean; permModify: boolean; permTransfer: boolean;
    permRemix: boolean; permTemplate: boolean; isPremium: boolean; polyCount: bigint; textureBytes: bigint;
  }): Promise<void>;
}

interface WorldScriptRow { scriptId: bigint; worldId: bigint; name: string; source: string; enabled: boolean }

type Tab = { kind: 'contract' } | { kind: 'world'; name: string } | { kind: 'npc'; npcId: string; name: string };
interface NpcUnitLite { npcId: bigint; worldId: bigint; name: string; scriptSrc: string; enabled: boolean; configJson: string }
type SideMode = 'none' | 'api' | 'ai' | 'objects' | 'outline';

const WORLD_GUIDE =
  'HOW SCRIPTS WORK: client-side Lua in the engine sandbox, run by EVERY visitor\'s client (visuals/experience only — shared truth moves through server contracts). ' +
  'The script RETURNS a table of hooks: on_init (once at load), on_tick(dt) (every frame — keep it light; 3 slow or erroring strikes disable the script locally), ' +
  'on_event(type, payload) (server contract events), on_interact(part_id, dist) (E within 3.5 m of a build part), on_signal(topic, payload, from) (npc.signal broadcasts). ' +
  'Bridge functions return FLAT values (numbers/strings/booleans) — never tables; iterate with 1-based count/index accessors (world.part_count + world.part_id(i)). ' +
  'Plain Lua 5.4 with math/string/table; no require/io/os/coroutines. world.log() is your print — it lands in the IDE console. Handles from world.spawn are numbers (0 = failed). ' +
  'Prefer world.tween/spin/scale_to (module-side animators) over per-frame math.';
const NPC_NOTE =
  ' npc.* functions only act inside an NPC unit\'s own script (Edit Mode → NPC tool); in a world script they return harmless defaults.';
const NPC_GUIDE =
  ' NPC SCRIPTS (npc.*): this buffer runs in its own slot bound to ONE npc_unit; "the player" is the local visitor (each visitor\'s client runs it for themselves). ' +
  'Shape: on_init → npc.set_state / npc.set_greeting / npc.add_topic / npc.set_sense; on_player_enter(name, dist) → npc.face_player, npc.emote, npc.say; ' +
  'on_talk_start(name) → (re)build topics for this player (npc.memory_get for stages); on_topic(key, name) → npc.say the answer (topics added WITH a reply answer themselves); ' +
  'on_say(name, text) → answer free text; on_choice(index, label, name) after npc.ask; on_arrive(x, z) after goto/wander/home; on_signal for NPC↔NPC; ' +
  'on_versona_reply(id, text) after npc.versona_ask; on_hour(h) for schedules. npc.timer(name, secs) inside on_tick replaces hand-rolled accumulators. ' +
  'npc.memory_set/get persists per player on their device. Movement follows the ground; positions are world metres; npc.home_x/z is the placed spot. ' +
  'npc.random/chance are per-NPC seeded (same on every client). The talk window shows npc.say lines while the player converses; otherwise npc.say is a speech bubble. ' +
  'TESTING: run_script binds to this NPC and can inject synthetic events (on_player_enter / on_say / on_topic / on_choice / on_interact …) — read the report (hooks called, errors, console, the NPC\'s action log) before claiming it works.';
const CONTRACT_GUIDE =
  'SERVER CONTRACT: handlers registered with contract.on(event, function(ev) ... end) are lowered to a deterministic, signed op-IR the server executes — no wall-clock, no randomness, no tables/closures beyond the handler bodies. ' +
  'state.get/set persists key→number; obj.get/set takes a NUMERIC LITERAL object id; world.emit(type, payload) reaches every client\'s world-script on_event. ' +
  'Capabilities (objects.read/write, world.events.emit, tickets.issue, player.pulse.read, economy.tip) must be declared, then granted by the owner; an ungranted use is denied. ' +
  'CPU/memory budgets throttle heavy contracts (never banned). Contracts cannot be previewed client-side — Deploy, then drive them from the L panel.';
type BottomTab = 'console' | 'problems';

interface IdeCommand { id: string; title: string; keys?: string; run: () => void }

export class LuaIdeModule implements IModule {
  readonly name = 'LuaIde';

  private win: UiWindow | null = null;
  private editor: EditorView | null = null;
  private note: HTMLDivElement | null = null;
  private capBoxes = new Map<string, HTMLInputElement>();
  private capsRow: HTMLDivElement | null = null;
  private pending: { source: string; caps: string; worldId: bigint } | null = null;

  private tab: Tab = { kind: 'contract' };
  private drafts = new Map<string, string>(); // tab key → unsaved buffer
  private serverDiags: LuaDiagnostic[] = [];
  private rail: HTMLDivElement | null = null;
  private bottomWrap: HTMLDivElement | null = null;
  private bottomTab: BottomTab = 'console';
  private consoleEl: HTMLDivElement | null = null;
  private problemsEl: HTMLDivElement | null = null;
  private problemsBadge: HTMLSpanElement | null = null;
  private consoleLines: string[] = []; // agent read_console + run_script capture
  private sideWrap: HTMLDivElement | null = null;
  private sideMode: SideMode = 'none';
  private mergeComp = new Compartment();
  private reviewBar: HTMLDivElement | null = null;
  private reviewBase = '';
  private editorHost: HTMLDivElement | null = null;
  private inlineChatEl: HTMLDivElement | null = null;
  private paletteEl: HTMLDivElement | null = null;
  private previewLive = false;
  private runBtn: HTMLButtonElement | null = null;
  private pickCleanup: (() => void) | null = null;
  private changeTimer: number | null = null;
  private aiChatLog: HTMLDivElement | null = null;
  private aiBusy = false;
  private aiAbort: AbortController | null = null;
  private chat: ChatMsg[] = [];
  private ghostOn = localStorage.getItem('atlas:copilot:ghost') === '1';

  constructor(private readonly freeform?: FreeformBuildModule, private readonly npcs?: NpcUnitModule) {}

  // API reference browser state + the last preview report (the copilot's test feedback)
  private apiQuery = '';
  private apiGroupKey: string | null = null;
  private apiCollapsed = new Set<string>();
  private lastPreviewStats: { name: string; hooks: Record<string, number>; errors: number; seconds: number; npcId?: string } | null = null;

  init(app: App): void {
    void app;
    window.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (target instanceof Element && target.closest('.cm-editor')) return;
      if (e.code === 'KeyI' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); this.toggle(); }
    });
    // Edit Mode drives the IDE: open on a specific script (world script or NPC
    // unit) without leaving the editor; close when the editor closes.
    bus.on('atlas:ide-open', (req) => {
      if (!this.allowed()) return;
      this.open();
      if (req.kind === 'world') {
        const exists = this.worldScripts().some((w) => w.name === req.name);
        if (!exists && req.source !== undefined && !this.drafts.has(`ws:${req.name}`)) this.drafts.set(`ws:${req.name}`, req.source);
        this.switchTab({ kind: 'world', name: req.name });
      } else {
        if (req.source !== undefined) this.drafts.set(`npc:${req.npcId}`, req.source); // the Edit Mode panel's unsaved text
        this.switchTab({ kind: 'npc', npcId: req.npcId, name: req.name });
      }
    });
    bus.on('atlas:ide-close', () => { if (this.win?.isOpen()) { this.stopPreview(); this.drafts.set(this.tabKey(), this.currentSource()); this.saveDraftsToStorage(); this.win.setOpen(false); } });
    bus.on('atlas:script-log', ({ script, msg, level }) => this.logLine(`[${script}] ${msg}`, level));
    bus.on('atlas:script-preview-stats', (st) => { this.lastPreviewStats = st; });
    (window as unknown as Record<string, unknown>).__ide = {
      open: () => { if (!this.allowed()) return false; this.open(); return true; },
      allowed: () => this.allowed(),
      isOpen: () => !!this.win?.isOpen(),
      close: () => this.win?.setOpen(false),
      tab: () => this.tab,
      openScript: (req: { kind: 'world'; name: string; source?: string } | { kind: 'npc'; npcId: string; name: string; source?: string }) => { bus.emit('atlas:ide-open', req); return this.tab; },
      source: () => this.currentSource(),
      tools: () => this.agentTools().map((t) => t.name),
      tool: async (name: string, args: Record<string, unknown> = {}) => { const t = this.agentTools().find((x) => x.name === name); if (!t) return `no tool ${name}`; return await t.run(args); },
    };
    bus.on('atlas:account-ready', () => {
      const conn = atlas.conn;
      conn?.db.asset.onInsert((_ctx, row) => {
        if (!this.pending || row.kind !== 'script') return;
        if (row.creator.toHexString() !== atlas.identityHex) return;
        const staged = this.pending;
        this.pending = null;
        if (atlas.currentWorldId !== staged.worldId) { this.setNote('World changed before the asset arrived — deploy again.', 'err'); return; }
        this.deployContract(row.assetId, staged.source, staged.caps, staged.worldId);
      });
      try {
        const ws = (conn?.db as unknown as { worldScript?: { onInsert(f: () => void): void; onUpdate(f: () => void): void; onDelete(f: () => void): void } }).worldScript;
        ws?.onInsert(() => this.renderRail());
        ws?.onUpdate(() => this.renderRail());
        ws?.onDelete(() => this.renderRail());
        // NPC creator units are IDE tabs too — keep the rail in step with their rows.
        conn?.db.npcUnit.onInsert(() => this.renderRail());
        conn?.db.npcUnit.onUpdate(() => this.renderRail());
        conn?.db.npcUnit.onDelete(() => this.renderRail());
      } catch { /* pre-publish server */ }
    });
    bus.on('atlas:world-entered', () => {
      this.tab = { kind: 'contract' };
      this.previewLive = false;
      this.syncRunBtn();
      this.loadDraftsFromStorage();
      this.renderRail();
      this.loadTab();
    });
  }

  // ─── data access ─────────────────────────────────────────────────────────────

  private worldScripts(): WorldScriptRow[] {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return [];
    try {
      const tbl = (conn.db as unknown as { worldScript?: { iter(): Iterable<WorldScriptRow> } }).worldScript;
      if (!tbl) return [];
      return [...tbl.iter()].filter((s) => s.worldId === wid).sort((a, b) => a.name.localeCompare(b.name));
    } catch { return []; }
  }

  /** NPC creator units in this world (their scripts are IDE tabs too). */
  private npcUnits(): NpcUnitLite[] {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return [];
    try { return [...conn.db.npcUnit.iter()].filter((u) => u.worldId === wid).sort((a, b) => (a.npcId < b.npcId ? -1 : 1)); } catch { return []; }
  }

  /**
   * The IDE is an EDIT MODE surface (F2): scripts change what everyone in the
   * world sees, so authoring happens where the other editing tools live. The
   * one exception is a droid terminal — programming a droid is its own mode.
   */
  private allowed(): boolean { return atlas.editMode || document.body.dataset.droidTerminal === '1'; }

  private dialect(): LuaDialect { return this.tab.kind === 'contract' ? 'contract' : 'worldscript'; }
  private tabKey(t: Tab = this.tab): string { return t.kind === 'contract' ? '::contract' : t.kind === 'npc' ? `npc:${t.npcId}` : `ws:${t.name}`; }

  private currentSource(): string { return this.editor?.state.doc.toString() ?? ''; }

  private setSource(text: string): void {
    if (!this.editor) return;
    this.editor.dispatch({ changes: { from: 0, to: this.editor.state.doc.length, insert: text } });
  }

  private insertAtCursor(text: string, cursorBack = 0): void {
    if (!this.editor) return;
    const pos = this.editor.state.selection.main.head;
    this.editor.dispatch({ changes: { from: pos, insert: text }, selection: { anchor: pos + text.length - cursorBack } });
    this.editor.focus();
  }

  // ─── draft persistence (autosave per world) ──────────────────────────────────

  private draftStoreKey(): string { return `atlas:ide:drafts:${atlas.currentWorldId ?? 0}`; }

  private loadDraftsFromStorage(): void {
    this.drafts.clear();
    try {
      const raw = localStorage.getItem(this.draftStoreKey());
      if (raw) for (const [k, v] of Object.entries(JSON.parse(raw) as Record<string, string>)) this.drafts.set(k, v);
    } catch { /* fresh */ }
  }

  private saveDraftsToStorage(): void {
    try {
      localStorage.setItem(this.draftStoreKey(), JSON.stringify(Object.fromEntries(this.drafts)));
    } catch { /* quota */ }
  }

  /** Is a tab's draft different from its deployed source? (the ● dirty dot) */
  private isDirty(t: Tab): boolean {
    const draft = this.drafts.get(this.tabKey(t));
    if (draft === undefined) return false;
    if (t.kind === 'contract') return draft !== TEMPLATE;
    if (t.kind === 'npc') { const u = this.npcUnits().find((x) => x.npcId.toString() === t.npcId); return u ? draft !== u.scriptSrc : false; }
    const row = this.worldScripts().find((s) => s.name === t.name);
    return row ? draft !== row.source : draft.length > 0;
  }

  // ─── window/UI assembly ──────────────────────────────────────────────────────

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    if (!this.allowed()) {
      bus.emit('atlas:status', { text: 'The Lua IDE opens in Edit Mode (F2) — select an object or NPC and use its Script tool' });
      return;
    }
    this.open();
  }

  private open(): void {
    if (this.win?.isOpen()) return;
    this.build();
    this.win!.setOpen(true);
    this.loadDraftsFromStorage();
    this.renderRail();
  }

  private btn(label: string, tip: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.title = tip;
    b.style.cssText = 'padding:5px 9px;background:#17233a;color:#cfe3ff;border:1px solid #35506a;border-radius:5px;font:12px system-ui;cursor:pointer';
    b.onclick = onClick;
    return b;
  }

  private build(): void {
    if (this.win) return;
    this.win = makeWindow({
      id: 'lua-ide', title: 'Lua IDE', icon: 'code',
      width: 980, height: 660, minWidth: 680, minHeight: 440,
      x: Math.max(8, window.innerWidth - 1000), y: 40,
    });
    const p = document.createElement('div');
    p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:#dde;font:12px/1.45 system-ui;box-sizing:border-box;position:relative';
    this.win.body.appendChild(p);

    // ── toolbar ──
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;align-items:center;padding:8px;border-bottom:1px solid #24344a;flex:0 0 auto;flex-wrap:wrap';
    this.runBtn = this.btn('▶ Run', 'Test the open script instantly — only you see it (Ctrl-Enter / F5)', () => this.runPreview());
    this.runBtn.style.background = '#1b3a52';
    this.runBtn.style.borderColor = '#2f6a94';
    bar.appendChild(this.runBtn);
    const deploy = this.btn('⬆ Deploy', 'Compile & deploy the open script for everyone (Ctrl-S)', () => this.compileAndDeploy());
    deploy.style.background = '#144a2f';
    deploy.style.borderColor = '#2c7a4f';
    deploy.style.color = '#d8ffe8';
    bar.appendChild(deploy);
    bar.appendChild(this.btn('Find', 'Search & replace (Ctrl-F)', () => { if (this.editor) openSearchPanel(this.editor); }));
    bar.appendChild(this.btn('Format', 'Format Document (Shift-Alt-F)', () => this.formatDocument()));
    bar.appendChild(this.btn('⌘', 'Command palette (Ctrl-Shift-P / F1)', () => this.openPalette()));
    bar.appendChild(this.btn('API', 'API reference (click to insert)', () => this.setSide(this.sideMode === 'api' ? 'none' : 'api')));
    bar.appendChild(this.btn('Objects', 'World objects — insert references, pick in 3D, per-object scripts', () => this.setSide(this.sideMode === 'objects' ? 'none' : 'objects')));
    bar.appendChild(this.btn('Outline', 'Symbols in the open script (Ctrl-Shift-O)', () => this.setSide(this.sideMode === 'outline' ? 'none' : 'outline')));
    bar.appendChild(this.btn('AI', 'Copilot: chat, inline edit (Ctrl-I), agent', () => this.setSide(this.sideMode === 'ai' ? 'none' : 'ai')));
    this.note = document.createElement('div');
    this.note.style.cssText = 'flex:1;opacity:.9;min-width:120px;text-align:right';
    bar.appendChild(this.note);
    p.appendChild(bar);

    // ── review bar (hidden until an AI edit review starts) ──
    this.reviewBar = document.createElement('div');
    this.reviewBar.style.cssText = 'display:none;gap:8px;align-items:center;padding:6px 8px;background:#1c2a14;border-bottom:1px solid #3c5a2a';
    p.appendChild(this.reviewBar);

    // ── main row: rail | editor | side ──
    const main = document.createElement('div');
    main.style.cssText = 'display:flex;flex:1;min-height:0';
    p.appendChild(main);

    this.rail = document.createElement('div');
    this.rail.style.cssText = 'width:150px;flex:0 0 auto;border-right:1px solid #24344a;overflow:auto;padding:6px;display:flex;flex-direction:column;gap:3px';
    main.appendChild(this.rail);

    this.editorHost = document.createElement('div');
    this.editorHost.style.cssText = 'flex:1;min-width:0;overflow:auto;position:relative';
    main.appendChild(this.editorHost);
    this.buildEditor(this.editorHost);

    this.sideWrap = document.createElement('div');
    this.sideWrap.style.cssText = 'width:320px;flex:0 0 auto;border-left:1px solid #24344a;overflow:hidden;display:none;flex-direction:column';
    main.appendChild(this.sideWrap);

    // ── bottom: Console | Problems tabs ──
    this.bottomWrap = document.createElement('div');
    this.bottomWrap.style.cssText = 'display:flex;flex-direction:column;height:120px;flex:0 0 auto;border-top:1px solid #24344a';
    const ch = document.createElement('div');
    ch.style.cssText = 'display:flex;gap:10px;align-items:center;padding:2px 8px';
    const mkTab = (id: BottomTab, label: string): HTMLSpanElement => {
      const s = document.createElement('span');
      s.textContent = label;
      s.style.cssText = 'cursor:pointer;padding:1px 4px;border-radius:4px';
      s.onclick = () => { this.bottomTab = id; this.renderBottom(); };
      return s;
    };
    const consoleTab = mkTab('console', 'Console');
    const problemsTab = mkTab('problems', 'Problems');
    this.problemsBadge = document.createElement('span');
    this.problemsBadge.style.cssText = 'background:#5a2a2a;border-radius:8px;padding:0 6px;font-size:10px';
    problemsTab.appendChild(this.problemsBadge);
    ch.appendChild(consoleTab);
    ch.appendChild(problemsTab);
    const spacer = document.createElement('span');
    spacer.style.flex = '1';
    ch.appendChild(spacer);
    const clear = document.createElement('span');
    clear.textContent = 'clear';
    clear.style.cssText = 'cursor:pointer;text-decoration:underline;opacity:.7';
    clear.onclick = () => { if (this.consoleEl) this.consoleEl.textContent = ''; this.consoleLines = []; };
    ch.appendChild(clear);
    this.bottomWrap.appendChild(ch);
    this.consoleEl = document.createElement('div');
    this.consoleEl.style.cssText = 'flex:1;overflow:auto;font:11px/1.5 ui-monospace,monospace;padding:2px 8px;white-space:pre-wrap';
    this.problemsEl = document.createElement('div');
    this.problemsEl.style.cssText = 'flex:1;overflow:auto;font:11px/1.6 ui-monospace,monospace;padding:2px 8px;display:none';
    this.bottomWrap.appendChild(this.consoleEl);
    this.bottomWrap.appendChild(this.problemsEl);
    p.appendChild(this.bottomWrap);

    // ── caps row (contract only) ──
    this.capsRow = document.createElement('div');
    this.capsRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px 12px;padding:6px 8px;border-top:1px solid #24344a;flex:0 0 auto';
    const capsLabel = document.createElement('span');
    capsLabel.style.opacity = '.75';
    capsLabel.textContent = 'Contract capabilities:';
    this.capsRow.appendChild(capsLabel);
    for (const cap of CAPS) {
      const label = document.createElement('label');
      label.style.cssText = 'display:inline-flex;align-items:center;gap:4px;cursor:pointer';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = cap === 'world.events.emit';
      label.appendChild(box);
      label.appendChild(document.createTextNode(cap));
      this.capsRow.appendChild(label);
      this.capBoxes.set(cap, box);
    }
    p.appendChild(this.capsRow);
    this.loadTab();
    this.renderBottom();
  }

  private renderBottom(): void {
    if (!this.consoleEl || !this.problemsEl) return;
    this.consoleEl.style.display = this.bottomTab === 'console' ? 'block' : 'none';
    this.problemsEl.style.display = this.bottomTab === 'problems' ? 'block' : 'none';
    if (this.bottomTab === 'problems') this.renderProblems();
  }

  private buildEditor(host: HTMLElement): void {
    const completions = (ctx: CompletionContext): CompletionResult | null => {
      const word = ctx.matchBefore(/[\w.]+$/);
      if (!word || (word.from === word.to && !ctx.explicit)) return null;
      const opts: Completion[] = [];
      for (const e of apiFor(this.dialect())) {
        if (e.insert) opts.push(snippetCompletion(e.insert, { label: e.name, detail: e.sig, info: e.doc, type: 'function' }));
        else opts.push({ label: e.name, detail: e.sig, info: e.doc, type: 'function', apply: e.name.includes('.') ? `${e.name}(` : e.name });
      }
      for (const k of LUA_KEYWORDS) opts.push({ label: k, type: 'keyword' });
      for (const k of LUA_STDLIB) opts.push({ label: k, type: 'namespace' });
      // structural snippets (the VS-style surround/skeleton set)
      opts.push(snippetCompletion('for i = 1, ${10} do\n  ${}\nend', { label: 'for', detail: 'numeric for loop', type: 'keyword' }));
      opts.push(snippetCompletion('if ${cond} then\n  ${}\nend', { label: 'if', detail: 'if block', type: 'keyword' }));
      opts.push(snippetCompletion('local function ${name}()\n  ${}\nend', { label: 'function', detail: 'local function', type: 'keyword' }));
      if (this.dialect() === 'worldscript') {
        opts.push(snippetCompletion(
          'return {\n  on_init = function()\n    ${}\n  end,\n  on_tick = function(dt)\n  end,\n  on_event = function(type, payload)\n  end,\n}',
          { label: 'hooks', detail: 'full hook-table skeleton', type: 'keyword' }));
      }
      // symbols already in the buffer (self-completion)
      for (const s of outlineLua(this.currentSource())) opts.push({ label: s.name, type: s.kind === 'local' ? 'variable' : 'function' });
      return { from: word.from, options: opts, validFor: /^[\w.]*$/ };
    };

    const hover = hoverTooltip((view, pos) => {
      const line = view.state.doc.lineAt(pos);
      const text = line.text;
      const off = pos - line.from;
      let s = off, e = off;
      while (s > 0 && /[\w.]/.test(text[s - 1])) s--;
      while (e < text.length && /[\w.]/.test(text[e])) e++;
      const name = text.slice(s, e);
      const entry = LUA_API.find((a) => a.name === name && a.dialects.includes(this.dialect()));
      if (!entry) return null;
      return {
        pos: line.from + s, end: line.from + e, above: true,
        create: () => {
          const dom = document.createElement('div');
          dom.style.cssText = 'max-width:380px;padding:7px 9px;background:#0d1420;border:1px solid #35506a;border-radius:6px;font:12px/1.45 system-ui;color:#dde';
          const sig = document.createElement('div');
          sig.style.cssText = 'font-family:ui-monospace,monospace;color:#9fd0ff;margin-bottom:4px';
          sig.textContent = entry.sig;
          const doc = document.createElement('div');
          doc.textContent = entry.doc;
          dom.appendChild(sig);
          dom.appendChild(doc);
          return { dom };
        },
      };
    });

    const lintSource = (view: EditorView): Diagnostic[] => {
      const src = view.state.doc.toString();
      const all = [...lintLua(src, this.dialect()), ...this.serverDiags];
      const doc = view.state.doc;
      this.updateProblemsBadge(all);
      return all.map((d) => {
        const ln = Math.min(Math.max(1, d.line), doc.lines);
        const line = doc.line(ln);
        const from = Math.min(line.from + Math.max(0, d.col - 1), line.to);
        return { from, to: line.to, severity: d.severity, message: d.message };
      });
    };

    this.editor = new EditorView({
      doc: TEMPLATE,
      parent: host,
      extensions: [
        basicSetup,
        keymap.of([
          indentWithTab,
          { key: 'Mod-s', preventDefault: true, run: () => { this.compileAndDeploy(); return true; } },
          { key: 'Mod-Enter', preventDefault: true, run: () => { this.runPreview(); return true; } },
          { key: 'F5', preventDefault: true, run: () => { this.runPreview(); return true; } },
          { key: 'Shift-F5', preventDefault: true, run: () => { this.stopPreview(); return true; } },
          { key: 'Mod-/', preventDefault: true, run: () => { this.toggleComment(); return true; } },
          { key: 'Shift-Alt-f', preventDefault: true, run: () => { this.formatDocument(); return true; } },
          { key: 'F2', preventDefault: true, run: () => { this.renameSymbol(); return true; } },
          { key: 'F12', preventDefault: true, run: () => { this.goToDefinition(); return true; } },
          { key: 'Mod-g', preventDefault: true, run: (v) => gotoLine(v) },
          { key: 'Mod-Shift-p', preventDefault: true, run: () => { this.openPalette(); return true; } },
          { key: 'F1', preventDefault: true, run: () => { this.openPalette(); return true; } },
          { key: 'Mod-i', preventDefault: true, run: () => { this.openInlineChat(); return true; } },
          { key: 'Mod-Shift-o', preventDefault: true, run: () => { this.setSide('outline'); return true; } },
        ]),
        StreamLanguage.define(lua),
        oneDark,
        search({ top: true }),
        autocompletion({ override: [completions] }),
        hover,
        linter(lintSource, { delay: 400 }),
        lintGutter(),
        this.mergeComp.of([]),
        ghostText({
          enabled: () => this.ghostOn && loadSettings().apiKey.length > 0,
          fetch: (prefix, suffix, signal) => completeInline(loadSettings(), apiPromptBlock(this.dialect()), prefix, suffix, signal),
        }) as never,
        EditorView.updateListener.of((u) => { if (u.docChanged) this.onDocChanged(); }),
        EditorView.domEventHandlers({
          mousedown: (e, view) => {
            if (!e.ctrlKey && !e.metaKey) return false;
            const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos === null) return false;
            e.preventDefault();
            this.goToDefinition(pos);
            return true;
          },
        }),
        EditorView.theme({ '&': { fontSize: '12px', height: '100%' } }),
      ],
    });
  }

  /** Debounced doc-change fan-out: autosave draft, refresh problems/outline/rail. */
  private onDocChanged(): void {
    if (this.changeTimer !== null) window.clearTimeout(this.changeTimer);
    this.changeTimer = window.setTimeout(() => {
      this.changeTimer = null;
      this.drafts.set(this.tabKey(), this.currentSource());
      this.saveDraftsToStorage();
      if (this.bottomTab === 'problems') this.renderProblems();
      if (this.sideMode === 'outline') this.renderSide();
      this.renderRail();
    }, 600);
  }

  // ─── run / stop preview (the live-test loop) ─────────────────────────────────

  private runPreview(): void {
    if (this.tab.kind === 'contract') {
      this.setNote('Contracts run SERVER-side — Deploy, then drive them from the L panel.', 'err');
      return;
    }
    const source = this.currentSource();
    const errs = lintLua(source, 'worldscript').filter((d) => d.severity === 'error');
    if (errs.length > 0) {
      this.setNote(`fix ${errs.length} error(s) first — line ${errs[0].line}: ${errs[0].message}`, 'err');
      this.bottomTab = 'problems';
      this.renderBottom();
      return;
    }
    bus.emit('atlas:script-preview', this.tab.kind === 'npc' ? { name: this.tab.name, source, npcId: this.tab.npcId } : { name: this.tab.name, source });
    this.previewLive = true;
    this.syncRunBtn();
    this.bottomTab = 'console';
    this.renderBottom();
    this.setNote('Preview running — only you see it. Deploy (Ctrl-S) to publish.', 'ok');
  }

  private stopPreview(): void {
    if (!this.previewLive) return;
    bus.emit('atlas:script-preview-stop', {});
    this.previewLive = false;
    this.syncRunBtn();
    this.setNote('Preview stopped.');
  }

  private syncRunBtn(): void {
    if (!this.runBtn) return;
    if (this.previewLive) {
      this.runBtn.textContent = '↻ Re-run';
      this.runBtn.title = 'Reload the preview with the current buffer (Ctrl-Enter). Shift-F5 stops it.';
    } else {
      this.runBtn.textContent = '▶ Run';
      this.runBtn.title = 'Test the open script instantly — only you see it (Ctrl-Enter / F5)';
    }
  }

  // ─── editor commands (format / rename / definition / comment) ────────────────

  private formatDocument(): void {
    if (!this.editor) return;
    const before = this.currentSource();
    const after = formatLua(before);
    if (after === before) { this.setNote('Already formatted.'); return; }
    const sel = this.editor.state.selection.main.head;
    this.setSource(after);
    this.editor.dispatch({ selection: { anchor: Math.min(sel, after.length) } });
    this.setNote('Document formatted.', 'ok');
  }

  private wordAtCursor(): string {
    if (!this.editor) return '';
    const { state } = this.editor;
    const pos = state.selection.main.head;
    const line = state.doc.lineAt(pos);
    const text = line.text;
    const off = pos - line.from;
    let s = off, e = off;
    while (s > 0 && /[\w.]/.test(text[s - 1])) s--;
    while (e < text.length && /[\w.]/.test(text[e])) e++;
    return text.slice(s, e);
  }

  private renameSymbol(): void {
    if (!this.editor) return;
    const name = this.wordAtCursor();
    if (!name || name.includes('.')) { this.setNote('Put the cursor on a local identifier to rename.', 'err'); return; }
    const occ = findOccurrences(this.currentSource(), name);
    if (occ.length === 0) return;
    const next = prompt(`Rename "${name}" (${occ.length} occurrence${occ.length === 1 ? '' : 's'}) to:`, name)?.trim();
    if (!next || next === name) return;
    if (!/^[A-Za-z_]\w*$/.test(next)) { this.setNote('Not a valid Lua identifier.', 'err'); return; }
    this.editor.dispatch({ changes: occ.map((o) => ({ from: o.from, to: o.to, insert: next })) });
    this.setNote(`Renamed ${occ.length} occurrence${occ.length === 1 ? '' : 's'}.`, 'ok');
  }

  private goToDefinition(pos?: number): void {
    if (!this.editor) return;
    if (pos !== undefined) this.editor.dispatch({ selection: { anchor: pos } });
    const name = this.wordAtCursor();
    if (!name) return;
    // API builtins: open the reference panel instead (their "definition" is docs).
    if (LUA_API.some((a) => a.name === name && a.dialects.includes(this.dialect()))) {
      this.setSide('api');
      this.setNote(`${name} is an API builtin — docs in the API panel.`);
      return;
    }
    const def = findDefinition(this.currentSource(), name.split('.')[0]);
    if (!def) { this.setNote(`No definition of "${name}" in this file.`, 'err'); return; }
    this.jumpTo(def.line, def.col);
  }

  private jumpTo(line: number, col = 1): void {
    if (!this.editor) return;
    const doc = this.editor.state.doc;
    const ln = doc.line(Math.min(Math.max(1, line), doc.lines));
    const anchor = Math.min(ln.from + Math.max(0, col - 1), ln.to);
    this.editor.dispatch({ selection: { anchor }, scrollIntoView: true });
    this.editor.focus();
  }

  private toggleComment(): void {
    if (!this.editor) return;
    const { state } = this.editor;
    const range = state.selection.main;
    const fromLine = state.doc.lineAt(range.from).number;
    const toLine = state.doc.lineAt(range.to).number;
    const lines = [];
    for (let n = fromLine; n <= toLine; n++) lines.push(state.doc.line(n));
    const allCommented = lines.every((l) => l.text.trim().length === 0 || l.text.trimStart().startsWith('--'));
    const changes = lines.flatMap((l) => {
      if (l.text.trim().length === 0) return [];
      if (allCommented) {
        const i = l.text.indexOf('--');
        const skip = l.text.slice(i + 2).startsWith(' ') ? 3 : 2;
        return [{ from: l.from + i, to: l.from + i + skip, insert: '' }];
      }
      const indent = l.text.length - l.text.trimStart().length;
      return [{ from: l.from + indent, insert: '-- ' }];
    });
    if (changes.length) this.editor.dispatch({ changes });
  }

  // ─── command palette ─────────────────────────────────────────────────────────

  private commands(): IdeCommand[] {
    return [
      { id: 'run', title: '▶ Run preview (test locally)', keys: 'Ctrl-Enter', run: () => this.runPreview() },
      { id: 'stop', title: '■ Stop preview', keys: 'Shift-F5', run: () => this.stopPreview() },
      { id: 'deploy', title: '⬆ Deploy (publish to everyone)', keys: 'Ctrl-S', run: () => this.compileAndDeploy() },
      { id: 'format', title: 'Format Document', keys: 'Shift-Alt-F', run: () => this.formatDocument() },
      { id: 'rename', title: 'Rename Symbol', keys: 'F2', run: () => this.renameSymbol() },
      { id: 'gotodef', title: 'Go to Definition', keys: 'F12', run: () => this.goToDefinition() },
      { id: 'comment', title: 'Toggle Line Comment', keys: 'Ctrl-/', run: () => this.toggleComment() },
      { id: 'find', title: 'Find & Replace', keys: 'Ctrl-F', run: () => { if (this.editor) openSearchPanel(this.editor); } },
      { id: 'gotoline', title: 'Go to Line…', keys: 'Ctrl-G', run: () => { if (this.editor) gotoLine(this.editor); } },
      { id: 'outline', title: 'Show Outline', keys: 'Ctrl-Shift-O', run: () => this.setSide('outline') },
      { id: 'objects', title: 'Show World Objects', run: () => this.setSide('objects') },
      { id: 'api', title: 'Show API Reference', run: () => this.setSide('api') },
      { id: 'ai', title: 'Show AI Copilot', run: () => this.setSide('ai') },
      { id: 'inline', title: 'Inline Chat (edit selection with AI)', keys: 'Ctrl-I', run: () => this.openInlineChat() },
      { id: 'problems', title: 'Show Problems', run: () => { this.bottomTab = 'problems'; this.renderBottom(); } },
      { id: 'console', title: 'Show Console', run: () => { this.bottomTab = 'console'; this.renderBottom(); } },
      { id: 'pick', title: '🎯 Pick object in world (insert part id)', run: () => this.startPick() },
      { id: 'newscript', title: 'New world script…', run: () => this.newScript() },
    ];
  }

  private openPalette(): void {
    if (!this.win || this.paletteEl) return;
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:absolute;inset:0;background:rgba(4,8,14,.55);z-index:30;display:flex;justify-content:center;align-items:flex-start;padding-top:48px';
    const box = document.createElement('div');
    box.style.cssText = 'width:420px;background:#0d1420;border:1px solid #35506a;border-radius:8px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.6)';
    const input = document.createElement('input');
    input.placeholder = 'Type a command…';
    input.style.cssText = 'width:100%;box-sizing:border-box;background:#111c2c;color:#dde;border:none;border-bottom:1px solid #24344a;padding:9px 12px;font:13px system-ui;outline:none';
    const list = document.createElement('div');
    list.style.cssText = 'max-height:300px;overflow:auto';
    box.appendChild(input);
    box.appendChild(list);
    overlay.appendChild(box);
    (this.win.body.firstElementChild as HTMLElement).appendChild(overlay);
    this.paletteEl = overlay;

    let selected = 0;
    let filtered = this.commands();
    const close = (): void => { overlay.remove(); this.paletteEl = null; this.editor?.focus(); };
    const render = (): void => {
      list.textContent = '';
      filtered.forEach((c, i) => {
        const row = document.createElement('div');
        row.style.cssText = `display:flex;justify-content:space-between;padding:6px 12px;cursor:pointer;${i === selected ? 'background:#1c2c44' : ''}`;
        const t = document.createElement('span');
        t.textContent = c.title;
        row.appendChild(t);
        if (c.keys) {
          const k = document.createElement('span');
          k.textContent = c.keys;
          k.style.cssText = 'opacity:.55;font:11px ui-monospace,monospace';
          row.appendChild(k);
        }
        row.onclick = () => { close(); c.run(); };
        row.onmouseenter = () => { selected = i; render(); };
        list.appendChild(row);
      });
    };
    input.oninput = () => {
      const q = input.value.toLowerCase();
      filtered = this.commands().filter((c) => c.title.toLowerCase().includes(q));
      selected = 0;
      render();
    };
    input.onkeydown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); selected = Math.min(filtered.length - 1, selected + 1); render(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); selected = Math.max(0, selected - 1); render(); }
      else if (e.key === 'Enter') { e.preventDefault(); const c = filtered[selected]; close(); c?.run(); }
    };
    overlay.onclick = (e) => { if (e.target === overlay) close(); };
    render();
    input.focus();
  }

  // ─── inline chat (Ctrl-I — AI edit of the selection, in place) ───────────────

  private openInlineChat(): void {
    if (!this.editor || !this.editorHost || this.inlineChatEl) return;
    if (!loadSettings().apiKey) { this.setSide('ai'); this.setNote('Set an API key in the AI panel to use inline chat.', 'err'); return; }
    const sel = this.editor.state.selection.main;
    const hasSel = sel.from !== sel.to;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:absolute;top:6px;left:24px;right:24px;z-index:20;background:#0d1420;border:1px solid #4a6a94;border-radius:7px;padding:7px;display:flex;gap:6px;box-shadow:0 6px 24px rgba(0,0,0,.55)';
    const input = document.createElement('input');
    input.placeholder = hasSel ? 'Describe how to change the SELECTION… (Esc closes)' : 'Describe how to change this script… (Esc closes)';
    input.style.cssText = 'flex:1;background:#111c2c;color:#dde;border:1px solid #35506a;border-radius:5px;padding:5px 8px;font:12px system-ui;outline:none';
    const go = this.btn('Go', 'Apply — reviewed as a diff', () => void submit());
    wrap.appendChild(input);
    wrap.appendChild(go);
    this.editorHost.appendChild(wrap);
    this.inlineChatEl = wrap;
    const close = (): void => { wrap.remove(); this.inlineChatEl = null; this.editor?.focus(); };
    const submit = async (): Promise<void> => {
      const q = input.value.trim();
      if (!q || this.aiBusy) return;
      const s = loadSettings();
      const full = this.currentSource();
      const region = hasSel ? full.slice(sel.from, sel.to) : full;
      go.textContent = '…';
      this.aiBusy = true;
      this.aiAbort = new AbortController();
      try {
        const prompt = hasSel
          ? `Rewrite ONLY this region of the script to satisfy: ${q}\n\nRegion:\n\`\`\`lua\n${region}\n\`\`\`\nOutput the COMPLETE revised region as ONE \`\`\`lua fenced block — nothing else, no omissions.`
          : `${q}\n\nRewrite the OPEN buffer to satisfy this. Output the COMPLETE revised file as ONE \`\`\`lua fenced block — no omissions.`;
        const reply = await chatStream(s, this.aiSystem(), [{ role: 'user', content: prompt }], () => { /* not streamed to UI */ }, this.aiAbort.signal);
        const block = this.lastLuaBlock(reply);
        if (!block) { this.setNote('The model returned no code block.', 'err'); return; }
        const proposed = hasSel ? full.slice(0, sel.from) + block.replace(/\n$/, '') + full.slice(sel.to) : block;
        this.startReview(proposed);
      } catch (e) {
        this.setNote(`inline chat error: ${(e as Error).message}`, 'err');
      } finally {
        this.aiBusy = false;
        this.aiAbort = null;
        close();
      }
    };
    input.onkeydown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Enter') { e.preventDefault(); void submit(); }
    };
    input.focus();
  }

  // ─── 🎯 pick-in-world (click a part in 3D → its id at the cursor) ────────────

  private startPick(): void {
    if (this.pickCleanup) { this.pickCleanup(); }
    const root = this.freeform?.root;
    const canvas = document.querySelector('canvas');
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!root || !canvas || !cam) { this.setNote('Pick needs the 3D world (enter a world first).', 'err'); return; }
    this.setNote('🎯 Click an object in the world… (Esc cancels)');
    const ray = new THREE.Raycaster();
    const onClick = (e: PointerEvent): void => {
      const r = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      ray.setFromCamera(ndc, cam);
      const hits = ray.intersectObjects(root.children, true);
      let id = '';
      for (const h of hits) {
        let o: THREE.Object3D | null = h.object;
        while (o) {
          if (typeof (o.userData as { ffPartId?: string }).ffPartId === 'string') { id = (o.userData as { ffPartId: string }).ffPartId; break; }
          o = o.parent;
        }
        if (id) break;
      }
      cleanup();
      if (!id) { this.setNote('No object under the cursor — try again.', 'err'); return; }
      this.insertAtCursor(`"${id}"`);
      this.setNote(`Inserted part id ${id}.`, 'ok');
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { cleanup(); this.setNote('Pick cancelled.'); } };
    const cleanup = (): void => {
      canvas.removeEventListener('pointerdown', onClick, true);
      window.removeEventListener('keydown', onKey, true);
      this.pickCleanup = null;
    };
    canvas.addEventListener('pointerdown', onClick, true);
    window.addEventListener('keydown', onKey, true);
    this.pickCleanup = cleanup;
  }

  // ─── script rail ─────────────────────────────────────────────────────────────

  private railEntry(label: string, active: boolean, onClick: () => void): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText = `padding:4px 7px;border-radius:5px;cursor:pointer;display:flex;align-items:center;gap:5px;` +
      (active ? 'background:#1c2c44;color:#e8f2fc' : 'color:#a9bdd4');
    const t = document.createElement('span');
    t.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
    t.textContent = label;
    d.appendChild(t);
    d.onclick = onClick;
    return d;
  }

  private newScript(): void {
    const name = prompt('Script name (per-world unique):', 'main')?.trim();
    if (!name) return;
    this.drafts.set(`ws:${name}`, WORLD_TEMPLATE);
    this.switchTab({ kind: 'world', name });
  }

  private renderRail(): void {
    if (!this.rail) return;
    this.rail.textContent = '';
    const head = document.createElement('div');
    head.style.cssText = 'opacity:.65;padding:2px 4px';
    head.textContent = 'Scripts';
    this.rail.appendChild(head);
    const dirtyDot = (t: Tab): string => (this.isDirty(t) ? ' ●' : '');
    this.rail.appendChild(this.railEntry(`⚙ server contract${dirtyDot({ kind: 'contract' })}`, this.tab.kind === 'contract', () => this.switchTab({ kind: 'contract' })));
    for (const s of this.worldScripts()) {
      const entry = this.railEntry(s.name + dirtyDot({ kind: 'world', name: s.name }), this.tab.kind === 'world' && this.tab.name === s.name, () => this.switchTab({ kind: 'world', name: s.name }));
      const en = document.createElement('input');
      en.type = 'checkbox';
      en.checked = s.enabled;
      en.title = 'enabled (runs for every visitor)';
      en.onclick = (e) => {
        e.stopPropagation();
        void (atlas.conn?.reducers as unknown as LuaReducers | undefined)
          ?.setWorldScript({ worldId: s.worldId, name: s.name, source: s.source, enabled: en.checked })
          .catch((err: Error) => this.setNote(err.message, 'err'));
      };
      entry.appendChild(en);
      const del = document.createElement('span');
      del.textContent = '×';
      del.title = 'delete script';
      del.style.cssText = 'cursor:pointer;opacity:.6;padding:0 2px';
      del.onclick = (e) => {
        e.stopPropagation();
        if (!confirm(`Delete world script "${s.name}"?`)) return;
        void (atlas.conn?.reducers as unknown as LuaReducers | undefined)
          ?.deleteWorldScript({ scriptId: s.scriptId })
          .then(() => { this.drafts.delete(`ws:${s.name}`); this.saveDraftsToStorage(); if (this.tab.kind === 'world' && this.tab.name === s.name) this.switchTab({ kind: 'contract' }); })
          .catch((err: Error) => this.setNote(err.message, 'err'));
      };
      entry.appendChild(del);
      this.rail.appendChild(entry);
    }
    // NPC creator units — their scripts live on npc_unit rows (Edit Mode → NPC).
    const units = this.npcUnits();
    if (units.length > 0) {
      const nh = document.createElement('div');
      nh.style.cssText = 'opacity:.65;padding:6px 4px 2px';
      nh.textContent = 'NPC scripts';
      this.rail.appendChild(nh);
      for (const u of units) {
        const id = u.npcId.toString();
        const entry = this.railEntry(`☺ ${u.name}${dirtyDot({ kind: 'npc', npcId: id, name: u.name })}`, this.tab.kind === 'npc' && this.tab.npcId === id, () => this.switchTab({ kind: 'npc', npcId: id, name: u.name }));
        const en = document.createElement('input');
        en.type = 'checkbox';
        en.checked = u.enabled;
        en.title = 'enabled (runs for every visitor)';
        en.onclick = (e) => {
          e.stopPropagation();
          void atlas.conn?.reducers.setNpcScript({ npcId: u.npcId, source: u.scriptSrc, enabled: en.checked }).catch((err: Error) => this.setNote(err.message, 'err'));
        };
        entry.appendChild(en);
        this.rail.appendChild(entry);
      }
    }
    // drafts that have no server row yet (new scripts pre-deploy)
    for (const key of this.drafts.keys()) {
      if (!key.startsWith('ws:')) continue;
      const name = key.slice(3);
      if (this.worldScripts().some((s) => s.name === name)) continue;
      const entry = this.railEntry(`${name} ●`, this.tab.kind === 'world' && this.tab.name === name, () => this.switchTab({ kind: 'world', name }));
      entry.style.opacity = '.85';
      this.rail.appendChild(entry);
    }
    const add = this.railEntry('＋ new world script', false, () => this.newScript());
    add.style.opacity = '.8';
    this.rail.appendChild(add);
  }

  private switchTab(t: Tab): void {
    if (this.reviewBase) this.finishReview(false); // leaving a review discards it
    this.drafts.set(this.tabKey(), this.currentSource());
    this.saveDraftsToStorage();
    this.tab = t;
    this.serverDiags = [];
    this.loadTab();
    this.renderRail();
    this.renderSide();
  }

  private loadTab(): void {
    if (!this.editor) return;
    const key = this.tabKey();
    const draft = this.drafts.get(key);
    if (draft !== undefined) { this.setSource(draft); }
    else if (this.tab.kind === 'contract') { this.setSource(TEMPLATE); }
    else if (this.tab.kind === 'npc') {
      const t = this.tab;
      const u = this.npcUnits().find((x) => x.npcId.toString() === t.npcId);
      this.setSource(u?.scriptSrc ?? '');
    } else {
      const row = this.worldScripts().find((s) => this.tab.kind === 'world' && s.name === this.tab.name);
      this.setSource(row ? row.source : WORLD_TEMPLATE);
    }
    if (this.capsRow) this.capsRow.style.display = this.tab.kind === 'contract' ? 'flex' : 'none';
  }

  // ─── console + problems + notes ──────────────────────────────────────────────

  private logLine(text: string, level: 'info' | 'error' = 'info'): void {
    this.consoleLines.push(text);
    if (this.consoleLines.length > 300) this.consoleLines.shift();
    if (!this.consoleEl) return;
    const d = document.createElement('div');
    d.style.color = level === 'error' ? '#ff9c9c' : '#cfe3ff';
    d.textContent = `${new Date().toLocaleTimeString()} ${text}`;
    this.consoleEl.appendChild(d);
    while (this.consoleEl.childElementCount > 200) this.consoleEl.firstElementChild?.remove();
    this.consoleEl.scrollTop = this.consoleEl.scrollHeight;
  }

  private updateProblemsBadge(diags: LuaDiagnostic[]): void {
    if (!this.problemsBadge) return;
    const errs = diags.filter((d) => d.severity === 'error').length;
    this.problemsBadge.textContent = diags.length ? ` ${diags.length}` : '';
    this.problemsBadge.style.background = errs > 0 ? '#5a2a2a' : '#4a4426';
    this.problemsBadge.style.display = diags.length ? 'inline' : 'none';
  }

  private renderProblems(): void {
    if (!this.problemsEl) return;
    const diags = [...lintLua(this.currentSource(), this.dialect()), ...this.serverDiags]
      .sort((a, b) => a.line - b.line);
    this.updateProblemsBadge(diags);
    this.problemsEl.textContent = '';
    if (diags.length === 0) {
      const d = document.createElement('div');
      d.style.opacity = '.6';
      d.textContent = 'No problems detected.';
      this.problemsEl.appendChild(d);
      return;
    }
    for (const dg of diags) {
      const row = document.createElement('div');
      row.style.cssText = 'cursor:pointer;padding:1px 0';
      row.textContent = `${dg.severity === 'error' ? '⛔' : '⚠'} line ${dg.line}: ${dg.message}`;
      row.style.color = dg.severity === 'error' ? '#ff9c9c' : '#ffd98c';
      row.onclick = () => this.jumpTo(dg.line, dg.col);
      this.problemsEl.appendChild(row);
    }
  }

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    if (!this.note) return;
    this.note.textContent = text;
    this.note.style.color = cls === 'err' ? '#ff9c9c' : cls === 'ok' ? '#9cffc4' : '#dde';
    if (cls === 'err') this.logLine(text, 'error');
  }

  // ─── deploy ──────────────────────────────────────────────────────────────────

  private compileAndDeploy(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null || !this.editor) return;
    if (this.reviewBase) this.finishReview(true); // deploying mid-review keeps what's on screen
    const reducers = conn.reducers as unknown as LuaReducers;
    const source = this.currentSource();
    // Pre-flight: block deploys with lint ERRORS (warnings pass).
    const errs = lintLua(source, this.dialect()).filter((d) => d.severity === 'error');
    if (errs.length > 0) {
      this.setNote(`fix ${errs.length} error(s) first — line ${errs[0].line}: ${errs[0].message}`, 'err');
      this.bottomTab = 'problems';
      this.renderBottom();
      if (this.editor) forceLinting(this.editor);
      return;
    }

    if (this.tab.kind === 'npc') {
      const t = this.tab;
      this.setNote(`Saving NPC script for ${t.name}…`);
      void conn.reducers.setNpcScript({ npcId: BigInt(t.npcId), source, enabled: true })
        .then(() => {
          this.setNote(`NPC "${t.name}" script live — it runs for every visitor now.`, 'ok');
          this.logLine(`deployed NPC script "${t.name}"`);
          if (this.previewLive) this.stopPreview();
          this.renderRail();
        })
        .catch((err: Error) => this.setNote(err.message, 'err'));
      return;
    }
    if (this.tab.kind === 'world') {
      if (typeof reducers.setWorldScript !== 'function') { this.setNote('Server needs publish + generate for setWorldScript.', 'err'); return; }
      const name = this.tab.name;
      this.setNote('Publishing world script…');
      void reducers.setWorldScript({ worldId, name, source, enabled: true })
        .then(() => {
          this.setNote(`World script "${name}" live — every visitor runs it now.`, 'ok');
          this.logLine(`deployed world script "${name}"`);
          if (this.previewLive) this.stopPreview(); // the deployed copy takes over
          this.renderRail(); // dirty dot clears
        })
        .catch((err: Error) => this.setNote(err.message, 'err'));
      return;
    }

    if (typeof reducers.compileLuaContract !== 'function') { this.setNote('Server needs publish + generate for compileLuaContract.', 'err'); return; }
    const caps = JSON.stringify([...this.capBoxes.entries()].filter(([, b]) => b.checked).map(([c]) => c));
    let assetId: bigint | null = null;
    for (const a of conn.db.asset.iter()) {
      if (a.kind === 'script' && a.creator.toHexString() === atlas.identityHex) { assetId = a.assetId; break; }
    }
    if (assetId !== null) { this.deployContract(assetId, source, caps, worldId); return; }
    this.pending = { source, caps, worldId };
    this.setNote('Creating a script asset…');
    void reducers.createAsset({
      kind: 'script', contentRef: 'atlas://worldlogic/contract',
      permCopy: false, permModify: true, permTransfer: false, permRemix: false, permTemplate: false,
      isPremium: false, polyCount: 0n, textureBytes: 0n,
    }).catch((err: Error) => { this.pending = null; this.setNote(`could not create script asset: ${err.message}`, 'err'); });
  }

  private deployContract(assetId: bigint, luaSource: string, capsRequested: string, worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Compiling on the server…');
    this.serverDiags = [];
    void (conn.reducers as unknown as LuaReducers)
      .compileLuaContract({ worldId, assetId, luaSource, capsRequested })
      .then(() => { this.setNote('Deployed — grant capabilities + run in the L panel.', 'ok'); this.logLine('contract deployed'); if (this.editor) forceLinting(this.editor); })
      .catch((err: Error) => {
        this.serverDiags = [diagnosticFromServerError(err.message)];
        this.setNote(err.message, 'err');
        this.bottomTab = 'problems';
        this.renderBottom();
        if (this.editor) forceLinting(this.editor); // surfaces "line N: …" inline
      });
  }

  // ─── side panels ─────────────────────────────────────────────────────────────

  private setSide(mode: SideMode): void {
    this.sideMode = mode;
    if (this.sideWrap) this.sideWrap.style.display = mode === 'none' ? 'none' : 'flex';
    this.renderSide();
  }

  private renderSide(): void {
    if (!this.sideWrap || this.sideMode === 'none') return;
    this.sideWrap.textContent = '';
    if (this.sideMode === 'api') this.renderApiPanel(this.sideWrap);
    else if (this.sideMode === 'objects') this.renderObjectsPanel(this.sideWrap);
    else if (this.sideMode === 'outline') this.renderOutlinePanel(this.sideWrap);
    else this.renderAiPanel(this.sideWrap);
  }

  private sideHead(root: HTMLElement, text: string): void {
    const head = document.createElement('div');
    head.style.cssText = 'padding:7px 9px;border-bottom:1px solid #24344a;font-weight:600';
    head.textContent = text;
    root.appendChild(head);
  }

  private renderApiPanel(root: HTMLElement): void {
    const d = this.dialect();
    const kindLabel = d === 'contract' ? 'server contract' : this.tab.kind === 'npc' ? `NPC script · ${this.tab.name}` : 'world script';
    this.sideHead(root, `API reference — ${kindLabel}`);
    const bar = document.createElement('div');
    bar.style.cssText = 'padding:6px 8px;border-bottom:1px solid #24344a;display:flex;flex-direction:column;gap:5px';
    const search = document.createElement('input');
    search.placeholder = 'search names & docs… (eye contact, bubble, fog, topic)';
    search.value = this.apiQuery;
    search.style.cssText = 'background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px 6px;font:12px system-ui';
    search.onkeydown = (e) => e.stopPropagation();
    const chips = document.createElement('div');
    chips.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px';
    bar.append(search, chips);
    root.appendChild(bar);
    const list = document.createElement('div');
    list.style.cssText = 'flex:1;overflow:auto;padding:6px';
    root.appendChild(list);
    const groups = apiGroups(d);
    const shortTitle = (t: string): string => t.split(' — ')[0].split(' (')[0];
    const chip = (label: string, active: boolean, title: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.title = title;
      b.style.cssText = `padding:2px 7px;border-radius:10px;font:11px system-ui;cursor:pointer;border:1px solid ${active ? '#6b9fe8' : '#2a3f5a'};background:${active ? '#2c4a72' : '#17233a'};color:#cfe3ff`;
      b.onclick = fn;
      return b;
    };
    const insert = (e: (typeof groups)[number]['entries'][number]): void => {
      const ins = e.insert ?? (e.name.includes('.') || e.name === 'input' || e.name === 'log' ? `${e.name}()` : e.name);
      this.insertAtCursor(ins, ins.endsWith(')') ? 1 : 0);
    };
    const entryEl = (e: (typeof groups)[number]['entries'][number]): HTMLDivElement => {
      const item = document.createElement('div');
      item.style.cssText = 'padding:5px 6px;border-radius:5px;cursor:pointer;margin-bottom:2px;border-left:2px solid transparent';
      item.title = 'Click to insert at the cursor';
      item.onmouseenter = () => { item.style.background = '#16233a'; item.style.borderLeftColor = '#6b9fe8'; };
      item.onmouseleave = () => { item.style.background = 'transparent'; item.style.borderLeftColor = 'transparent'; };
      const sig = document.createElement('div');
      sig.style.cssText = 'font-family:ui-monospace,monospace;color:#9fd0ff;font-size:11px;word-break:break-word';
      sig.textContent = e.sig;
      const doc = document.createElement('div');
      doc.style.cssText = 'opacity:.78;font-size:11px;line-height:1.4';
      doc.textContent = e.doc;
      item.append(sig, doc);
      item.onclick = () => insert(e);
      return item;
    };
    const render = (): void => {
      chips.textContent = '';
      chips.appendChild(chip('All', this.apiGroupKey === null, 'every group', () => { this.apiGroupKey = null; render(); }));
      for (const g of groups) chips.appendChild(chip(`${shortTitle(g.title)} (${g.entries.length})`, this.apiGroupKey === g.key, g.title, () => { this.apiGroupKey = this.apiGroupKey === g.key ? null : g.key; render(); }));
      list.textContent = '';
      const q = this.apiQuery.trim();
      if (q) {
        // Relevance-ranked flat list (names + docs), scoped to the active group.
        const allowed = this.apiGroupKey ? new Set(groups.find((g) => g.key === this.apiGroupKey)?.entries.map((e) => e.name) ?? []) : null;
        const hits = searchApi(d, q, 60).filter((e) => !allowed || allowed.has(e.name));
        const h = document.createElement('div');
        h.style.cssText = 'opacity:.65;padding:2px 4px 6px';
        h.textContent = hits.length ? `${hits.length} match${hits.length === 1 ? '' : 'es'} for “${q}”` : `nothing matches “${q}” — try another word (docs are searched too)`;
        list.appendChild(h);
        for (const e of hits) list.appendChild(entryEl(e));
        return;
      }
      if (d === 'worldscript' && this.tab.kind !== 'npc' && (this.apiGroupKey === null || this.apiGroupKey.startsWith('npc-'))) {
        const n = document.createElement('div');
        n.style.cssText = 'opacity:.65;padding:2px 4px 6px;font-size:11px';
        n.textContent = 'npc.* functions act only inside an NPC unit\'s own script (Edit Mode → NPC); world.* works in both.';
        list.appendChild(n);
      }
      for (const g of groups) {
        if (this.apiGroupKey && g.key !== this.apiGroupKey) continue;
        const collapsed = this.apiCollapsed.has(g.key) && !this.apiGroupKey;
        const head = document.createElement('div');
        head.style.cssText = 'display:flex;align-items:center;gap:6px;padding:6px 4px 3px;cursor:pointer;color:#9fb4cc;font:600 11px system-ui;letter-spacing:.03em;user-select:none';
        head.textContent = `${collapsed ? '▸' : '▾'} ${g.title} · ${g.entries.length}`;
        head.onclick = () => { if (this.apiCollapsed.has(g.key)) this.apiCollapsed.delete(g.key); else this.apiCollapsed.add(g.key); render(); };
        list.appendChild(head);
        if (collapsed) continue;
        for (const e of g.entries) list.appendChild(entryEl(e));
      }
    };
    search.oninput = () => { this.apiQuery = search.value; render(); };
    render();
  }

  // ─── Objects panel (the script↔object bridge) ────────────────────────────────

  private renderObjectsPanel(root: HTMLElement): void {
    this.sideHead(root, 'World objects — reference in code');
    const tools = document.createElement('div');
    tools.style.cssText = 'display:flex;gap:5px;padding:6px 8px;border-bottom:1px solid #24344a';
    tools.appendChild(this.btn('🎯 Pick in world', 'Click a part in the 3D view — its id lands at the cursor', () => this.startPick()));
    root.appendChild(tools);
    const list = document.createElement('div');
    list.style.cssText = 'flex:1;overflow:auto;padding:6px';
    root.appendChild(list);

    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    interface PartRowLite { partId: bigint; worldId: bigint; primType: string; posX: number; posY: number; posZ: number }
    let parts: PartRowLite[] = [];
    try {
      if (conn && wid !== null) parts = [...(conn.db.part as unknown as { iter(): Iterable<PartRowLite> }).iter()].filter((p) => p.worldId === wid).sort((a, b) => (a.partId < b.partId ? -1 : 1));
    } catch { /* pre-publish */ }

    const section = (title: string): void => {
      const h = document.createElement('div');
      h.style.cssText = 'opacity:.65;padding:4px 2px 2px';
      h.textContent = title;
      list.appendChild(h);
    };

    section(`Build parts (${parts.length}) — id · type · use world.part_*`);
    if (parts.length === 0) {
      const d = document.createElement('div');
      d.style.cssText = 'opacity:.6;padding:4px 2px';
      d.textContent = 'No parts yet — place objects in Build Mode (B).';
      list.appendChild(d);
    }
    for (const pt of parts.slice(0, 120)) {
      const id = pt.partId.toString();
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:5px;padding:3px 4px;border-radius:5px';
      row.onmouseenter = () => { row.style.background = '#16233a'; };
      row.onmouseleave = () => { row.style.background = 'transparent'; };
      const label = document.createElement('span');
      label.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font:11px ui-monospace,monospace';
      label.textContent = `#${id} ${pt.primType} (${pt.posX.toFixed(0)}, ${pt.posY.toFixed(0)}, ${pt.posZ.toFixed(0)})`;
      row.appendChild(label);
      const insId = this.btn('id', `Insert "${id}" at the cursor`, () => this.insertAtCursor(`"${id}"`));
      insId.style.padding = '2px 6px';
      row.appendChild(insId);
      const newScript = this.btn('✎', 'New script wired to this object', () => {
        const name = `part_${id}`;
        this.drafts.set(`ws:${name}`, objectTemplate(id, pt.primType));
        this.saveDraftsToStorage();
        this.switchTab({ kind: 'world', name });
        this.setNote(`Script "${name}" drafted — ▶ Run to test, Deploy to publish.`, 'ok');
      });
      newScript.style.padding = '2px 6px';
      row.appendChild(newScript);
      list.appendChild(row);
    }

    // Prefab keys (world.spawn catalog)
    let prefabs: string[] = [];
    try {
      if (conn) prefabs = [...new Set([...conn.db.prefabModel.iter()].map((r) => r.prefabKey))].sort();
    } catch { /* pre-publish */ }
    section(`Prefabs (${prefabs.length}) — world.spawn keys`);
    for (const key of prefabs.slice(0, 200)) {
      const row = document.createElement('div');
      row.style.cssText = 'padding:2px 4px;border-radius:5px;cursor:pointer;font:11px ui-monospace,monospace;color:#9fd0ff';
      row.textContent = key;
      row.title = 'Insert at cursor';
      row.onmouseenter = () => { row.style.background = '#16233a'; };
      row.onmouseleave = () => { row.style.background = 'transparent'; };
      row.onclick = () => this.insertAtCursor(`"${key}"`);
      list.appendChild(row);
    }
  }

  private renderOutlinePanel(root: HTMLElement): void {
    this.sideHead(root, 'Outline — symbols (click to jump)');
    const list = document.createElement('div');
    list.style.cssText = 'flex:1;overflow:auto;padding:6px';
    root.appendChild(list);
    const symbols = outlineLua(this.currentSource());
    if (symbols.length === 0) {
      const d = document.createElement('div');
      d.style.cssText = 'opacity:.6;padding:4px 2px';
      d.textContent = 'No symbols yet.';
      list.appendChild(d);
    }
    for (const s of symbols) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;align-items:center;padding:3px 5px;border-radius:5px;cursor:pointer';
      row.onmouseenter = () => { row.style.background = '#16233a'; };
      row.onmouseleave = () => { row.style.background = 'transparent'; };
      const icon = document.createElement('span');
      icon.textContent = s.kind === 'hook' ? '⚡' : s.kind === 'function' ? 'ƒ' : '•';
      icon.style.cssText = `width:14px;text-align:center;color:${s.kind === 'hook' ? '#ffd98c' : s.kind === 'function' ? '#9fd0ff' : '#8aa'}`;
      const name = document.createElement('span');
      name.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
      name.textContent = s.name;
      const ln = document.createElement('span');
      ln.style.cssText = 'opacity:.5;font-size:10px';
      ln.textContent = `:${s.line}`;
      row.appendChild(icon);
      row.appendChild(name);
      row.appendChild(ln);
      row.onclick = () => this.jumpTo(s.line);
      list.appendChild(row);
    }
  }

  // ─── AI copilot panel ────────────────────────────────────────────────────────

  private clearPreviewStats(): void { this.lastPreviewStats = null; }
  private takePreviewStats(): { name: string; hooks: Record<string, number>; errors: number; seconds: number; npcId?: string } | null { return this.lastPreviewStats; }

  /** Resolve "npc:<id>", an id, or a unit name to a live unit. */
  private resolveNpc(arg: string): { id: string; name: string } | null {
    if (!this.npcs) return null;
    const raw = arg.trim().replace(/^npc:/, '');
    const byId = this.npcs.get(raw);
    if (byId) return { id: byId.id, name: byId.name() };
    for (const id of this.npcs.ids()) { const u = this.npcs.get(id); if (u && u.name().toLowerCase() === raw.toLowerCase()) return { id, name: u.name() }; }
    return null;
  }

  private aiSystem(): string {
    const d = this.dialect();
    const isNpc = this.tab.kind === 'npc';
    const scripts = this.worldScripts();
    let ctx = '';
    let budget = 18_000;
    for (const s of scripts) {
      const chunk = `\n--- script "${s.name}" (${s.enabled ? 'enabled' : 'disabled'}) ---\n${s.source.slice(0, 5000)}`;
      if (budget - chunk.length < 0) { ctx += `\n--- script "${s.name}" (omitted for budget — read_script to load) ---`; continue; }
      ctx += chunk;
      budget -= chunk.length;
    }
    let npcCtx = '';
    for (const u of this.npcUnits()) {
      const chunk = `\n--- NPC "${u.name}" (id ${u.npcId}, ${u.enabled ? 'enabled' : 'disabled'}) ---\n${u.scriptSrc.slice(0, 4000)}`;
      if (budget - chunk.length < 0) { npcCtx += `\n--- NPC "${u.name}" (id ${u.npcId}; omitted for budget — read_script "npc:${u.npcId}") ---`; continue; }
      npcCtx += chunk;
      budget -= chunk.length;
    }
    // Live object context — the copilot can reference REAL part ids.
    let objCtx = '';
    try {
      const conn = atlas.conn;
      const wid = atlas.currentWorldId;
      if (conn && wid !== null) {
        interface PartRowLite { partId: bigint; worldId: bigint; primType: string; posX: number; posY: number; posZ: number }
        const parts = [...(conn.db.part as unknown as { iter(): Iterable<PartRowLite> }).iter()].filter((p) => p.worldId === wid).slice(0, 40);
        if (parts.length) objCtx = `\nBuild parts in this world (id type x,y,z — reference via world.part_*("id")):\n` +
          parts.map((p) => `  "${p.partId}" ${p.primType} ${p.posX.toFixed(1)},${p.posY.toFixed(1)},${p.posZ.toFixed(1)}`).join('\n');
      }
    } catch { /* pre-publish */ }
    // Live people context — scripted units + versonas the npc.* calls can address.
    let peopleCtx = '';
    if (this.npcs) {
      const ids = this.npcs.ids();
      if (ids.length) peopleCtx += '\nScripted NPC units right now (id · name · state · x,z):\n' + ids.map((id) => { const u = this.npcs!.get(id)!; return `  "${id}" ${u.name()} [${u.state()}] ${u.x.toFixed(1)},${u.z.toFixed(1)}`; }).join('\n');
      const vs = this.npcs.versonas().slice(0, 12);
      if (vs.length) peopleCtx += '\nVersonas in this world (id · name · job · state — npc.versona_* ids):\n' + vs.map((v) => `  "${v.id}" ${v.name} ${v.job} [${v.state}]`).join('\n');
    }
    let npcSelf = '';
    if (this.tab.kind === 'npc') {
      const t = this.tab;
      const u = this.npcUnits().find((x) => x.npcId.toString() === t.npcId);
      const live = this.npcs?.get(t.npcId);
      npcSelf = `\nTHIS NPC: "${t.name}" id ${t.npcId}${u ? ` · config JSON ${u.configJson}` : ''}${live ? ` · live state "${live.state()}" at ${live.x.toFixed(1)},${live.z.toFixed(1)} · topics ${live.topics.length} · player ${live.playerDist().toFixed(1)} m away` : ''}`;
    }
    const npcTab = this.tab.kind === 'npc' ? this.tab : null;
    const focus = npcTab
      ? `the Lua script of the scripted NPC "${npcTab.name}" (an npc_unit — the NPC creator)`
      : d === 'contract' ? 'a SERVER-side behavioral contract (deterministic, capability-gated op-IR)'
        : 'a CLIENT-side world script (engine-sandboxed visuals that run on every visitor)';
    const guide = d === 'contract' ? CONTRACT_GUIDE : WORLD_GUIDE + (isNpc ? NPC_GUIDE : NPC_NOTE);
    const examples = isNpc ? `\nSTARTER TEMPLATES the NPC tool ships (proven shapes):${NPC_TEMPLATES.map((t) => `\n--- ${t.label}: ${t.hint} ---\n${t.source}`).join('')}` : '';
    return (
      `You are the Lua copilot inside Cosmos, a persistent multiplayer world. The user edits ${focus}.${npcSelf}\n\n` +
      `${guide}\n\n` +
      `THE ONLY AVAILABLE API (never invent functions beyond these + plain Lua 5.4 with math/string/table; use search_api for details):\n${apiPromptBlock(d)}\n` +
      `${examples}\n\nAll world scripts in this world:${ctx || ' (none yet)'}\nNPC scripts in this world:${npcCtx || ' (none yet)'}\n${objCtx}${peopleCtx}\n` +
      `The OPEN buffer is "${this.tab.kind === 'contract' ? 'server contract' : this.tab.name}":\n\`\`\`lua\n${this.currentSource().slice(0, 8000)}\n\`\`\`\n` +
      `Be concise. Code answers in \`\`\`lua fences. Always return the hook table; never call hooks yourself.`
    );
  }

  private renderAiPanel(root: HTMLElement): void {
    const s = loadSettings();
    // settings row
    const cfg = document.createElement('div');
    cfg.style.cssText = 'padding:7px;border-bottom:1px solid #24344a;display:flex;flex-direction:column;gap:5px';
    const row1 = document.createElement('div');
    row1.style.cssText = 'display:flex;gap:5px';
    const provider = document.createElement('select');
    for (const pv of ['anthropic', 'openai'] as const) {
      const o = document.createElement('option');
      o.value = pv;
      o.textContent = pv === 'anthropic' ? 'Claude (Anthropic)' : 'OpenAI';
      provider.appendChild(o);
    }
    provider.value = s.provider;
    provider.style.cssText = 'flex:1;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px';
    const model = document.createElement('input');
    model.value = s.model;
    model.placeholder = 'model';
    model.style.cssText = 'width:130px;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px';
    provider.onchange = () => { model.value = DEFAULT_MODEL[provider.value as 'anthropic' | 'openai']; persist(); };
    row1.appendChild(provider);
    row1.appendChild(model);
    cfg.appendChild(row1);
    const row2 = document.createElement('div');
    row2.style.cssText = 'display:flex;gap:5px;align-items:center';
    const key = document.createElement('input');
    key.type = 'password';
    key.value = s.apiKey;
    key.placeholder = 'API key (stored only in this browser)';
    key.style.cssText = 'flex:1;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px';
    row2.appendChild(key);
    const ghost = document.createElement('label');
    ghost.style.cssText = 'display:inline-flex;gap:3px;align-items:center;cursor:pointer;white-space:nowrap';
    const gbox = document.createElement('input');
    gbox.type = 'checkbox';
    gbox.checked = this.ghostOn;
    gbox.onchange = () => { this.ghostOn = gbox.checked; localStorage.setItem('atlas:copilot:ghost', gbox.checked ? '1' : '0'); };
    ghost.appendChild(gbox);
    ghost.appendChild(document.createTextNode('ghost text'));
    row2.appendChild(ghost);
    cfg.appendChild(row2);
    const persist = (): void => saveSettings({ provider: provider.value as 'anthropic' | 'openai', apiKey: key.value.trim(), model: model.value.trim() || DEFAULT_MODEL[provider.value as 'anthropic' | 'openai'] });
    key.onchange = persist;
    model.onchange = persist;
    root.appendChild(cfg);

    // chat log
    this.aiChatLog = document.createElement('div');
    this.aiChatLog.style.cssText = 'flex:1;overflow:auto;padding:8px;display:flex;flex-direction:column;gap:8px';
    root.appendChild(this.aiChatLog);
    for (const m of this.chat) {
      if (m.role === 'assistant') this.richBubble(m.content);
      else this.chatBubble(m.role, m.content);
    }

    // input + actions
    const foot = document.createElement('div');
    foot.style.cssText = 'padding:7px;border-top:1px solid #24344a;display:flex;flex-direction:column;gap:5px';
    const input = document.createElement('textarea');
    input.rows = 2;
    input.placeholder = 'Ask, or describe an edit… (/explain /fix /doc /run · Enter sends)';
    input.style.cssText = 'background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:5px;resize:vertical;font:12px system-ui';
    input.onkeydown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send('chat'); } };
    foot.appendChild(input);
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:5px;flex-wrap:wrap';
    const send = (mode: 'chat' | 'edit' | 'agent'): void => {
      let q = input.value.trim();
      if (!q || this.aiBusy) return;
      input.value = '';
      // slash commands (the VS Code Copilot shorthand)
      if (q === '/run') { this.runPreview(); return; }
      if (q.startsWith('/explain')) { q = `${q.slice(8).trim() || 'Explain what this script does, briefly.'}`; mode = 'chat'; }
      else if (q.startsWith('/fix')) { q = `${q.slice(4).trim() || 'Fix the problems in the open buffer.'}`; mode = 'edit'; }
      else if (q.startsWith('/doc')) { q = `Add concise doc comments to the open buffer. ${q.slice(4).trim()}`; mode = 'edit'; }
      void this.runAi(mode, q);
    };
    actions.appendChild(this.btn('Send', 'Chat with full world-code context', () => send('chat')));
    actions.appendChild(this.btn('✎ Edit buffer', 'AI rewrites the open script — review as a diff', () => send('edit')));
    actions.appendChild(this.btn('⚙ Agent', 'Multi-step: reads scripts/objects, lints, RUNS code, stages edits', () => send('agent')));
    actions.appendChild(this.btn('/explain', 'Explain the open script', () => { input.value = '/explain'; send('chat'); }));
    actions.appendChild(this.btn('/fix', 'Fix problems in the open script', () => { input.value = '/fix'; send('edit'); }));
    const stop = this.btn('■ Stop', 'Abort the running request', () => this.aiAbort?.abort());
    actions.appendChild(stop);
    foot.appendChild(actions);
    root.appendChild(foot);
  }

  private chatBubble(role: 'user' | 'assistant' | 'tool', text: string): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText =
      role === 'user' ? 'background:#1c2c44;border-radius:8px;padding:6px 9px;align-self:flex-end;max-width:95%;white-space:pre-wrap'
        : role === 'tool' ? 'opacity:.65;font:11px ui-monospace,monospace;white-space:pre-wrap'
          : 'background:#131c2b;border-radius:8px;padding:6px 9px;max-width:100%;white-space:pre-wrap;font-family:ui-monospace,monospace;font-size:11.5px';
    d.textContent = text;
    this.aiChatLog?.appendChild(d);
    if (this.aiChatLog) this.aiChatLog.scrollTop = this.aiChatLog.scrollHeight;
    return d;
  }

  /** Assistant bubble with per-```lua-block Insert / Apply buttons (Copilot-style). */
  private richBubble(text: string): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText = 'background:#131c2b;border-radius:8px;padding:6px 9px;max-width:100%;display:flex;flex-direction:column;gap:5px';
    const parts = text.split(/```(?:lua)?\n([\s\S]*?)```/g);
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        if (parts[i].trim()) {
          const t = document.createElement('div');
          t.style.cssText = 'white-space:pre-wrap;font-size:12px';
          t.textContent = parts[i].trim();
          d.appendChild(t);
        }
      } else {
        const code = parts[i];
        const box = document.createElement('div');
        box.style.cssText = 'background:#0b111c;border:1px solid #24344a;border-radius:6px;overflow:hidden';
        const pre = document.createElement('pre');
        pre.style.cssText = 'margin:0;padding:7px 9px;overflow:auto;max-height:220px;font:11px/1.5 ui-monospace,monospace;color:#cfe3ff';
        pre.textContent = code.replace(/\n$/, '');
        const barEl = document.createElement('div');
        barEl.style.cssText = 'display:flex;gap:5px;padding:4px 7px;border-top:1px solid #24344a';
        const ins = this.btn('Insert at cursor', 'Insert this block at the cursor', () => this.insertAtCursor(code));
        ins.style.padding = '2px 7px';
        const apply = this.btn('Apply as edit', 'Review this block replacing the whole buffer as a diff', () => this.startReview(code));
        apply.style.padding = '2px 7px';
        barEl.appendChild(ins);
        barEl.appendChild(apply);
        box.appendChild(pre);
        box.appendChild(barEl);
        d.appendChild(box);
      }
    }
    this.aiChatLog?.appendChild(d);
    if (this.aiChatLog) this.aiChatLog.scrollTop = this.aiChatLog.scrollHeight;
    return d;
  }

  /** Extract the last ```lua fenced block from a model reply. */
  private lastLuaBlock(text: string): string | null {
    const m = [...text.matchAll(/```(?:lua)?\n([\s\S]*?)```/g)];
    return m.length ? m[m.length - 1][1] : null;
  }

  private async runAi(mode: 'chat' | 'edit' | 'agent', q: string): Promise<void> {
    const s: CopilotSettings = loadSettings();
    if (!s.apiKey) { this.chatBubble('assistant', 'Set an API key above first — it stays in this browser only.'); return; }
    this.aiBusy = true;
    this.aiAbort = new AbortController();
    this.chatBubble('user', q);
    this.chat.push({ role: 'user', content: q });
    try {
      if (mode === 'chat') {
        const bubble = this.chatBubble('assistant', '…');
        let acc = '';
        const full = await chatStream(s, this.aiSystem(), this.chat, (delta) => { acc += delta; bubble.textContent = acc; if (this.aiChatLog) this.aiChatLog.scrollTop = this.aiChatLog.scrollHeight; }, this.aiAbort.signal);
        this.chat.push({ role: 'assistant', content: full });
        bubble.remove();
        this.richBubble(full); // re-render with per-block Insert/Apply
      } else if (mode === 'edit') {
        const bubble = this.chatBubble('assistant', 'rewriting…');
        const prompt =
          `${q}\n\nRewrite the OPEN buffer to satisfy this. Output the COMPLETE revised file as ONE \`\`\`lua fenced block — no other code blocks, no omissions ("-- rest unchanged" is forbidden).`;
        let acc = '';
        const full = await chatStream(s, this.aiSystem(), [...this.chat.slice(0, -1), { role: 'user', content: prompt }], (delta) => { acc += delta; bubble.textContent = acc.slice(-600); }, this.aiAbort.signal);
        this.chat.push({ role: 'assistant', content: full });
        const proposed = this.lastLuaBlock(full);
        if (!proposed) { bubble.textContent = full; this.chatBubble('assistant', '(no lua block found — nothing to review)'); }
        else {
          const st = diffStats(diffLines(this.currentSource(), proposed));
          bubble.textContent = `Proposed edit: +${st.added} −${st.removed}. Review in the editor — accept/reject each chunk.`;
          this.startReview(proposed);
        }
      } else {
        await this.runAgentMode(s, q);
      }
    } catch (e) {
      this.chatBubble('assistant', `error: ${(e as Error).message}`);
    } finally {
      this.aiBusy = false;
      this.aiAbort = null;
    }
  }

  private async runAgentMode(s: CopilotSettings, q: string): Promise<void> {
    const tools = this.agentTools();
    const final = await runAgent(
      s,
      this.aiSystem() + '\nYou are in AGENT mode: use tools to inspect the world\'s scripts, PEOPLE (list_npcs) and OBJECTS, search_api when unsure of a function, lint your work, then RUN it in the live sandbox (run_script — bind to the NPC and inject synthetic events to exercise proximity/dialogue hooks) and READ the report (hooks called, lua_errors, console, npc_actions) before claiming anything works. Iterate until the report is clean. Writes only ever STAGE a review — never claim you deployed.',
      q,
      tools,
      {
        onText: (t) => { this.richBubble(t); this.chat.push({ role: 'assistant', content: t }); },
        onTool: (name, args) => this.chatBubble('tool', `→ ${name}(${JSON.stringify(args).slice(0, 140)})`),
        onToolResult: (name, out) => this.chatBubble('tool', `← ${name}: ${out.slice(0, 140)}`),
      },
      12,
      this.aiAbort?.signal,
    );
    void final;
  }

  /** The copilot's agent tools (also the probe's `__ide.tool(name, args)` surface). */
  private agentTools(): ToolDef[] {
    const unitList = (): Array<{ id: string; name: string; state: string; x: number; z: number; topics: number; enabled: boolean }> => {
      const rows = this.npcUnits();
      return (this.npcs?.ids() ?? rows.map((r) => r.npcId.toString())).map((id) => {
        const u = this.npcs?.get(id);
        const row = rows.find((r) => r.npcId.toString() === id);
        return { id, name: u?.name() ?? row?.name ?? '?', state: u?.state() ?? '', x: Math.round((u?.x ?? 0) * 10) / 10, z: Math.round((u?.z ?? 0) * 10) / 10, topics: u?.topics.length ?? 0, enabled: row?.enabled ?? false };
      });
    };
    const tools: ToolDef[] = [
      {
        name: 'list_scripts',
        description: 'List every script in this world: world scripts (name, enabled, bytes) AND NPC scripts (npc id, name, enabled, bytes), plus which buffer is open. Use before reading or editing.',
        params: {}, run: () => JSON.stringify({
          open: this.tab.kind === 'contract' ? 'server-contract' : this.tab.kind === 'npc' ? `npc:${this.tab.npcId} (${this.tab.name})` : this.tab.name,
          world_scripts: this.worldScripts().map((w) => ({ name: w.name, enabled: w.enabled, bytes: w.source.length })),
          npc_scripts: this.npcUnits().map((u) => ({ id: u.npcId.toString(), name: u.name, enabled: u.enabled, bytes: u.scriptSrc.length })),
        }),
      },
      {
        name: 'read_script',
        description: 'Read full Lua source: a world script by name, an NPC script by "npc:<id>" or its name, or "open-buffer" / "server-contract" for the editor contents.',
        params: { name: { type: 'string', description: 'world script name · npc:<id> · NPC name · open-buffer · server-contract' } }, required: ['name'],
        run: (a) => {
          const name = String(a.name ?? '');
          if (name === 'open-buffer' || name === 'server-contract') return this.currentSource();
          const ws = this.worldScripts().find((w) => w.name === name);
          if (ws) return ws.source;
          const npc = this.resolveNpc(name);
          if (npc) return this.npcUnits().find((u) => u.npcId.toString() === npc.id)?.scriptSrc ?? '';
          return `no script named "${name}" (use list_scripts)`;
        },
      },
      {
        name: 'list_objects',
        description: 'List the world\'s BUILD PARTS (id, type, position — referenced via world.part_*("id")) and the prefab keys world.spawn accepts. Use to wire scripts to real objects.',
        params: {}, run: () => {
          const conn = atlas.conn;
          const wid = atlas.currentWorldId;
          interface PartRowLite { partId: bigint; worldId: bigint; primType: string; posX: number; posY: number; posZ: number }
          let parts: Array<{ id: string; type: string; x: number; y: number; z: number }> = [];
          let prefabs: string[] = [];
          try {
            if (conn && wid !== null) {
              parts = [...(conn.db.part as unknown as { iter(): Iterable<PartRowLite> }).iter()].filter((p) => p.worldId === wid).slice(0, 60)
                .map((p) => ({ id: p.partId.toString(), type: p.primType, x: p.posX, y: p.posY, z: p.posZ }));
              prefabs = [...new Set([...conn.db.prefabModel.iter()].map((r) => r.prefabKey))].slice(0, 100);
            }
          } catch { /* pre-publish */ }
          return JSON.stringify({ parts, prefabs });
        },
      },
      {
        name: 'list_npcs',
        description: 'List the PEOPLE: scripted NPC units (id, name, live state, position, topic count, enabled) and the versonas in this world (id, name, job, state, position) — the ids npc.versona_* / npc.other_* take. Pass detail="<npc id or name>" for one unit\'s full snapshot (greeting, topics, recent actions).',
        params: { detail: { type: 'string', description: 'optional: an NPC id or name for a full snapshot' } },
        run: (a) => {
          const d = String(a.detail ?? '');
          if (d) { const r = this.resolveNpc(d); const u = r ? this.npcs?.get(r.id) : undefined; return u ? JSON.stringify(u.snapshot()) : `no NPC "${d}"`; }
          const versonas = (this.npcs?.versonas() ?? []).slice(0, 40).map((v) => ({ id: v.id, name: v.name, job: v.job, state: v.state, x: Math.round(v.x), z: Math.round(v.z) }));
          return JSON.stringify({ npcs: unitList(), versonas, player: { x: Math.round(atlas.localX * 10) / 10, z: Math.round(atlas.localZ * 10) / 10, name: atlas.username } });
        },
      },
      {
        name: 'search_api',
        description: 'Search the Lua API catalog (names AND docs) for the open dialect — e.g. "eye contact", "speech bubble", "fog", "topic". Returns the best matches with exact signatures and documentation. Use it whenever unsure a function exists or how it behaves.',
        params: { query: { type: 'string', description: 'words to look for' } }, required: ['query'],
        run: (a) => {
          const hits = searchApi(this.dialect(), String(a.query ?? ''), 12);
          return hits.length ? hits.map((e) => `${e.sig}\n    ${e.doc}`).join('\n') : 'no matches — try other words; list groups: ' + apiGroups(this.dialect()).map((g) => g.title).join(' | ');
        },
      },
      {
        name: 'lint_source',
        description: 'Run the IDE linter on Lua source (structure, unknown API calls, dialect rules). Returns a JSON array of diagnostics — empty means clean. ALWAYS lint before staging an edit.',
        params: { source: { type: 'string', description: 'lua source to check' }, dialect: { type: 'string', description: '"worldscript" or "contract"' } }, required: ['source'],
        run: (a) => JSON.stringify(lintLua(String(a.source ?? ''), (a.dialect === 'contract' ? 'contract' : 'worldscript'))),
      },
      {
        name: 'run_script',
        description: 'RUN Lua in the LIVE local sandbox (only this client sees it) and return a full test report: hooks called with counts, Lua errors, console output (world.log / npc.say lines), and — when bound to an NPC — the unit\'s state/position/topics before & after plus its action log (say, walk, emote, topics, incoming hooks). Omit `source` to run the open buffer. `npc` binds the run to a scripted NPC (default: the open NPC tab\'s unit). `events` injects synthetic hooks at times, e.g. [{"at":0.5,"hook":"on_player_enter","args":["Tester",3]},{"at":1.2,"hook":"on_say","args":["Tester","hello"]},{"at":2,"hook":"on_topic","args":["weather","Tester"]}] — the way to exercise proximity/dialogue paths without a human walking up.',
        params: {
          source: { type: 'string', description: 'lua source to run (default: the open buffer)' },
          seconds: { type: 'string', description: 'how long to run, 0.5..15 (default 2.5)' },
          npc: { type: 'string', description: 'NPC id or name to bind to (default: the open NPC tab)' },
          events: { type: 'string', description: 'JSON array of {at: seconds, hook: "on_…", args: [...]} to inject' },
        },
        run: async (a) => {
          const src = String(a.source ?? '') || this.currentSource();
          const secs = Math.min(15, Math.max(0.5, Number(a.seconds) || 2.5));
          const npcArg = String(a.npc ?? '');
          let npcId: string | undefined;
          if (npcArg) { const r = this.resolveNpc(npcArg); if (!r) return `no NPC "${npcArg}" — list_npcs shows them`; npcId = r.id; }
          else if (this.tab.kind === 'npc') npcId = this.tab.npcId;
          let events: Array<{ at: number; hook: string; args: unknown[] }> | undefined;
          if (a.events !== undefined && a.events !== null && String(a.events).trim() !== '') {
            try {
              const raw = typeof a.events === 'string' ? JSON.parse(a.events) as unknown : a.events;
              if (!Array.isArray(raw)) return 'events must be a JSON array of {at, hook, args}';
              events = raw as Array<{ at: number; hook: string; args: unknown[] }>;
            } catch { return 'events must be valid JSON: [{"at":0.5,"hook":"on_player_enter","args":["Tester",3]}]'; }
          }
          const unit = npcId ? this.npcs?.get(npcId) : undefined;
          const before = this.consoleLines.length;
          const snapBefore = unit?.snapshot();
          const recentBefore = unit ? unit.recent.length : 0;
          this.clearPreviewStats();
          bus.emit('atlas:script-preview', { name: 'agent-test', source: src, npcId, events });
          this.previewLive = true; this.syncRunBtn();
          await new Promise((r) => setTimeout(r, secs * 1000));
          bus.emit('atlas:script-preview-stop', {});
          this.previewLive = false; this.syncRunBtn();
          const stats = this.takePreviewStats(); // (a method read — control-flow narrowing would see the null assignment above)
          const consoleOut = this.consoleLines.slice(before);
          const loadFailed = consoleOut.some((l) => /failed to load/i.test(l));
          const after = unit?.snapshot();
          const strip = (sn?: Record<string, unknown>): Record<string, unknown> | undefined => { if (!sn) return undefined; const { recent: _r, ...rest } = sn; void _r; return rest; };
          const report = {
            ran_seconds: secs,
            bound_npc: unit ? { id: unit.id, name: unit.name() } : null,
            loaded: !loadFailed,
            hooks_called: stats?.hooks ?? {},
            lua_errors: stats?.errors ?? 0,
            console: consoleOut,
            npc_before: strip(snapBefore),
            npc_after: strip(after),
            npc_actions: unit ? unit.recent.slice(recentBefore) : undefined,
            verdict: loadFailed ? 'DID NOT LOAD — syntax error (see console)' : (stats?.errors ?? 0) > 0 ? 'RAN WITH LUA ERRORS — see console' : Object.keys(stats?.hooks ?? {}).length === 0 ? 'loaded but no hooks ran' : 'ran clean',
          };
          return JSON.stringify(report);
        },
      },
      {
        name: 'read_console',
        description: 'Read the last IDE console lines (world.log / npc output, Lua errors, watchdog strikes, preview summaries) — see what already happened before re-running.',
        params: { lines: { type: 'string', description: 'how many lines from the end (default 40)' } },
        run: (a) => this.consoleLines.slice(-Math.min(200, Number(a.lines) || 40)).join('\n') || '(console empty)',
      },
      {
        name: 'stage_edit',
        description: 'Stage a COMPLETE revised Lua file for the user to review as a diff (this never writes to the server — the user applies + deploys). target: a world script name, an NPC ("npc:<id>" or name), or "open-buffer" for the editor.',
        params: {
          target: { type: 'string', description: 'world script name · npc:<id> · NPC name · open-buffer' },
          source: { type: 'string', description: 'the FULL revised file (no omissions)' },
          note: { type: 'string', description: 'one-line summary of the change' },
        }, required: ['target', 'source'],
        run: (a) => {
          const target = String(a.target ?? 'open-buffer');
          const source = String(a.source ?? '');
          const note = String(a.note ?? '');
          if (target !== 'open-buffer') {
            const row = this.worldScripts().find((w) => w.name === target);
            const npc = row ? null : this.resolveNpc(target);
            if (row && !(this.tab.kind === 'world' && this.tab.name === target)) this.switchTab({ kind: 'world', name: target });
            else if (npc && !(this.tab.kind === 'npc' && this.tab.npcId === npc.id)) this.switchTab({ kind: 'npc', npcId: npc.id, name: npc.name });
          }
          const st = diffStats(diffLines(this.currentSource(), source));
          this.startReview(source);
          this.chatBubble('tool', `staged: ${target} (+${st.added} −${st.removed}) ${note}`);
          return `staged for review (+${st.added} −${st.removed}); the user will accept/reject chunks and deploy`;
        },
      },
    ];
    return tools;
  }

  // ─── AI edit review (unified merge view) ─────────────────────────────────────

  private startReview(proposed: string): void {
    if (!this.editor || !this.reviewBar) return;
    if (this.reviewBase) this.finishReview(false);
    this.reviewBase = this.currentSource();
    this.setSource(proposed);
    this.editor.dispatch({
      effects: this.mergeComp.reconfigure(unifiedMergeView({ original: this.reviewBase, mergeControls: true }) as never),
    });
    this.reviewBar.style.display = 'flex';
    this.reviewBar.textContent = '';
    const label = document.createElement('span');
    label.textContent = 'AI edit review — accept/reject each chunk in the gutter, then:';
    this.reviewBar.appendChild(label);
    this.reviewBar.appendChild(this.btn('✓ Keep result', 'Keep what is on screen (accepted chunks)', () => this.finishReview(true)));
    this.reviewBar.appendChild(this.btn('▶ Test result', 'Keep what is on screen AND run it as a preview', () => { this.finishReview(true); this.runPreview(); }));
    this.reviewBar.appendChild(this.btn('✗ Discard all', 'Revert the whole edit', () => this.finishReview(false)));
  }

  private finishReview(keep: boolean): void {
    if (!this.editor || !this.reviewBar) return;
    const base = this.reviewBase;
    this.reviewBase = '';
    this.editor.dispatch({ effects: this.mergeComp.reconfigure([]) });
    if (!keep && base) this.setSource(base);
    this.reviewBar.style.display = 'none';
    if (keep) this.setNote('AI edit kept — ▶ Run tests it, Deploy (Ctrl-S) publishes it.', 'ok');
  }

  dispose(): void {
    this.aiAbort?.abort();
    this.pickCleanup?.();
    this.editor?.destroy();
    this.win?.root.remove();
    this.win = null;
  }
}
